import React from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Divider,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { type CreateUserPayload, type UserRole } from "../utils/api";
import { useAppDispatch } from "../store/hooks";
import { createUserThunk } from "../features/users/usersSlice";

const defaultFormState: CreateUserPayload = {
  name: "",
  email: "",
  password: "",
  role: "member",
  phoneNumber: "",
  address: "",
};

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formValues, setFormValues] =
    React.useState<CreateUserPayload>(defaultFormState);
  const [saving, setSaving] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: name === "role" ? (value as UserRole) : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formValues.password?.trim()) {
      await Swal.fire({
        title: "Password required",
        text: "Please provide a temporary password for the new user.",
        icon: "warning",
        confirmButtonColor: "#0f62fe",
      });
      return;
    }
    setSaving(true);
    try {
      const payload: CreateUserPayload = {
        name: formValues.name,
        email: formValues.email,
        password: formValues.password,
        role: formValues.role,
        phoneNumber: formValues.phoneNumber || undefined,
        address: formValues.address || undefined,
      };

      const created = await dispatch(createUserThunk(payload)).unwrap();
      navigate(`/user/${created._id}`);
    } catch {
      // Errors handled via toast in thunk
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center">
      <Paper
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 900,
          p: { xs: 3, md: 4 },
          borderRadius: "4px",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={1}>
          Add user
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Provide the details below to create a new workspace member.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Full name"
              name="name"
              fullWidth
              value={formValues.name}
              onChange={handleFieldChange}
              required
            />
            <TextField
              label="Role"
              name="role"
              select
              fullWidth
              value={formValues.role}
              onChange={handleFieldChange}
            >
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              value={formValues.email}
              onChange={handleFieldChange}
              required
            />
            <TextField
              label="Phone number"
              name="phoneNumber"
              fullWidth
              value={formValues.phoneNumber}
              onChange={handleFieldChange}
            />
          </Stack>
          <TextField
            label="Address"
            name="address"
            fullWidth
            value={formValues.address}
            onChange={handleFieldChange}
          />
          <TextField
            label="Temporary password"
            name="password"
            type={showPassword ? "text" : "password"}
            helperText="Provide a temporary password so the user can log in and update it later."
            fullWidth
            value={formValues.password}
            onChange={handleFieldChange}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            onClick={() => navigate("/user")}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Create user"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserCreatePage;
